// KeepInTouchSection2.jsx
import React, { useState } from "react";

import clsx from "clsx";
import Link from "next/link";
import { useParams } from "next/navigation";

import { useTranslation } from "~/app/hooks/useTranslation";
import { buildLocalizedHref } from "~/app/lib/routeUtils";
import { submitFormMessage } from "~/common/api/formMessagesClient";
import Input from "~/common/components/Input";
import Modal from "~/common/components/Modal";
import PhoneInputField from "~/common/components/PhoneInputField";
import { isEmailFormatValid, isFreeEmailDomain } from "~/common/utils/email";
import { formatPhoneInput, getDefaultPhoneCountry, isPhoneValid, normalizePhoneNumber } from "~/common/utils/phone";

import styles from "./KeepInTouchSection2.module.scss";

function SendMessageForm() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    typeOfQuestion: "",
    message: "",
    category: "sales",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { locale } = useParams();
  const [phoneCountry, setPhoneCountry] = useState(() => getDefaultPhoneCountry(locale));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? formatPhoneInput(value, phoneCountry) : value,
    }));
  };

  const handleCountryChange = (nextCountry) => {
    setPhoneCountry(nextCountry);
    setFormData((prev) => ({
      ...prev,
      phone: prev.phone ? formatPhoneInput(prev.phone, nextCountry) : prev.phone,
    }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: null }));
    }
  };

  function validate(values, normalizedPhone) {
    const errs = {};
    if (!values.firstName || values.firstName.trim().length < 2) {
      errs.firstName = t("common.errors.invalidFirstName") || "Invalid";
    }
    if (!values.lastName || values.lastName.trim().length < 2) {
      errs.lastName = t("common.errors.invalidLastName") || "Invalid";
    }
    const email = typeof values.email === "string" ? values.email.trim() : "";
    if (!email || !isEmailFormatValid(email)) {
      errs.email = t("common.errors.invalidEmail") || "Invalid";
    } else if (isFreeEmailDomain(email)) {
      errs.email = t("common.errors.freeEmail") || "Invalid";
    }
    if (values.phone.trim() && (!normalizedPhone || !isPhoneValid(values.phone, phoneCountry))) {
      errs.phone = t("common.errors.invalidPhone") || "Invalid";
    }
    if (!values.category) {
      errs.category = t("common.errors.required") || "Required";
    }
    if (!values.message || values.message.trim().length < 5) {
      errs.message = t("common.errors.messageTooShort") || "Too short";
    }
    return errs;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const normalizedPhone = normalizePhoneNumber(formData.phone, phoneCountry);
    const vErrors = validate(formData, normalizedPhone);
    setErrors(vErrors);
    if (Object.keys(vErrors).length) return;
    try {
      setSubmitting(true);
      setErrors({});
      const metadata = {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: typeof window !== "undefined" ? { w: window.screen.width, h: window.screen.height } : null,
        appLocale: locale,
        locale: typeof navigator !== "undefined" ? navigator.language : null,
        context: "keep-in-touch-form",
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
        href: typeof window !== "undefined" ? window.location.href : null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      };
      await submitFormMessage({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: normalizedPhone,
        category: formData.category,
        message: formData.message.trim(),
        metadata,
      });
      setModalOpen(true);
      setFormData((p) => ({ ...p, message: "" }));
    } catch (err) {
      if (err.validation) setErrors(err.validation);
      else setErrors({ form: err.message || "Error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.row50}>
        <Input
          id="firstName"
          name="firstName"
          label={t("keepInTouch.section2.form.firstName")}
          required
          value={formData.firstName}
          onChange={handleChange}
          error={errors.firstName}
        />
      </div>
      <div className={styles.row50}>
        <Input
          id="lastName"
          name="lastName"
          label={t("keepInTouch.section2.form.lastName")}
          required
          value={formData.lastName}
          onChange={handleChange}
          error={errors.lastName}
        />
      </div>

      <div className={styles.row50}>
        <Input
          id="email"
          name="email"
          type="email"
          label={t("keepInTouch.section2.form.email")}
          required
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
        />
      </div>
      <div className={styles.row50}>
        <PhoneInputField
          id="phone"
          name="phone"
          label={t("keepInTouch.section2.form.phone")}
          value={formData.phone}
          onChange={handleChange}
          error={errors.phone}
          locale={locale}
          country={phoneCountry}
          onCountryChange={handleCountryChange}
          inputMode="tel"
          autoComplete="tel"
        />
      </div>
      <div className={styles.row100}>
        <Input
          name="category"
          label={t("keepInTouch.section2.form.typeOfQuery")}
          options={[
            { value: "sales", label: t("keepInTouch.section2.form.options.sales") },
            { value: "demo", label: t("keepInTouch.section2.form.options.demo") },
            { value: "tech", label: t("keepInTouch.section2.form.options.technology") },
            { value: "support", label: t("keepInTouch.section2.form.options.support") },
            { value: "other", label: t("keepInTouch.section2.form.options.other") },
          ]}
          value={formData.category}
          onChange={handleChange}
          required
          error={errors.category}
        />
      </div>
      <div className={styles.row100}>
        <Input
          id="message"
          name="message"
          label={t("keepInTouch.section2.form.yourMessage")}
          multiline
          minRows={8}
          maxRows={10}
          required
          value={formData.message}
          onChange={handleChange}
          error={errors.message}
        />
      </div>
      <div className={styles.inputWrapper}>
        <button type="submit" disabled={submitting}>
          {submitting ? t("common.submitting") || "..." : t("keepInTouch.section2.form.sendMessage")}
        </button>
        {errors.form && <p style={{ color: "#c00", fontSize: 12 }}>{errors.form}</p>}

        <p className={styles.terms}>
          {t("keepInTouch.section2.form.termsPart1")}&nbsp;
          <Link href={buildLocalizedHref(locale, "privacy-policy", { tab: "terms-of-use" })}>
            {t("keepInTouch.section2.form.termsOfService")}
          </Link>
          &nbsp;
          {t("keepInTouch.section2.form.termsPart2")}&nbsp;
          <Link href={buildLocalizedHref(locale, "privacy-policy", { tab: "privacy-policy" })}>
            {t("keepInTouch.section2.form.privacyNotice")}
          </Link>
          &nbsp;
          {t("keepInTouch.section2.form.termsPart3")}
        </p>
      </div>
      <Modal
        idPrefix="form-message-keep-in-touch"
        title={t("common.thankYou")}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <button btnType="default" onClick={() => setModalOpen(false)}>
            {t("common.ok")}
          </button>
        }
      >
        <p>{t("common.formSubmitted") || "Message received. We will contact you shortly."}</p>
      </Modal>
    </form>
  );
}

const KeepInTouchSection2 = () => {
  const { t } = useTranslation();
  return (
    <div className={clsx(styles.section, "card")}>
      <div className={styles.content}>
        <h2>{t("keepInTouch.section2.title")}</h2>
        <p>{t("keepInTouch.section2.description")}</p>
      </div>
      <SendMessageForm />
    </div>
  );
};

export default KeepInTouchSection2;
